// apps/scheduler-worker/src/components/TaskDetailCard.tsx
import { format, parseISO } from 'date-fns';
import { Clock, Briefcase, Calendar, AlertTriangle } from 'lucide-react';
import { formatDuration } from 'scheduler-lib';
import type { TaskDetailResponse } from '../lib/api';
import { TaskStatusBadge } from './TaskStatusBadge';
import { getTaskProgress, PROGRESS_COLORS } from '../lib/progress';

interface TaskDetailCardProps {
  task: TaskDetailResponse['data'];
}

export function TaskDetailCard({ task }: TaskDetailCardProps) {
  const formatScheduledDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not scheduled';
    try {
      return format(parseISO(dateStr), 'EEE, MMM d, h:mm a');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h1 className="text-xl font-semibold text-gray-900">
            {task.partNumber}
          </h1>
          <TaskStatusBadge status={task.status} />
        </div>
        <p className="text-sm text-gray-500 capitalize">
          {task.laborBucket.replace(/_/g, ' ')}
        </p>
      </div>

      {/* Details */}
      <div className="p-4 space-y-4">
        {/* Skill */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Required Skill</p>
            <p className="text-sm font-medium text-gray-900">
              {task.requiredSkill.name}
            </p>
          </div>
        </div>

        {/* Estimates + Progress */}
        <EstimateWithProgress task={task} />

        {/* Scheduled */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <Calendar className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Scheduled</p>
            <p className="text-sm font-medium text-gray-900">
              {formatScheduledDate(task.scheduledStart)}
            </p>
          </div>
        </div>

        {/* Blocked By */}
        {task.blockedBy && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Blocked: {task.blockedBy.type}
              </p>
              <p className="text-sm text-amber-700">
                {task.blockedBy.details}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EstimateWithProgress({ task }: { task: TaskDetailResponse['data'] }) {
  const midpoint = Math.round((task.estimatedMinutesMin + task.estimatedMinutesMax) / 2);
  const progress = task.status === 'in_progress'
    ? getTaskProgress(task.actualMinutes, midpoint)
    : getTaskProgress(0, midpoint); // non-in_progress tasks never show the bar

  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
        <Clock className="w-5 h-5 text-gray-600" />
      </div>
      <div className="flex-1">
        <p className="text-xs text-gray-500">Estimate</p>
        <p className="text-sm font-medium text-gray-900">
          {formatDuration(task.estimatedMinutesMin)} - {formatDuration(task.estimatedMinutesMax)}
        </p>
        {progress.showBar && (
          <>
            <div className="mt-1.5 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${PROGRESS_COLORS[progress.color]}`}
                style={{ width: `${Math.min(progress.percentage, 100)}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {formatDuration(task.actualMinutes)} logged ({progress.percentage}%)
            </p>
          </>
        )}
      </div>
    </div>
  );
}
